import React, { memo } from 'react';
import { Check, Calendar, MapPin, Clock, Sparkles } from 'lucide-react';
import { Service, Location } from '../../types';
import { T, rgba } from '../../utils/adminTheme';

interface SuccessMessageProps {
  service: Service | null;
  location: Location | null;
  date: Date | null;
  time: string | null;
  bookingId?: number | null;
  customerEmail?: string;
  onClose: () => void;
  onAddAnother?: () => void;
}

const DetailRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <div style={{
      width: 34,
      height: 34,
      background: rgba(T.accent, 0.1),
      borderRadius: T.r.md,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: T.accent,
    }}>
      {icon}
    </div>
    <div>
      <p style={{ margin: 0, fontSize: 11, color: T.muted, fontFamily: T.body, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: 14, color: T.text, fontFamily: T.body, fontWeight: 600 }}>
        {value}
      </p>
    </div>
  </div>
);

const SuccessMessage = memo(({ service, location, date, time, bookingId, customerEmail, onClose, onAddAnother }: SuccessMessageProps) => {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: 16,
    }}>
      <div style={{
        background: T.surface,
        borderRadius: T.r.lg,
        border: `1px solid ${T.line}`,
        boxShadow: '0 24px 64px rgba(0,0,0,0.16)',
        maxWidth: 420,
        width: '100%',
        overflow: 'hidden',
      }}>
        {/* Erfolgs-Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2d7a52 0%, #1a5c3d 100%)',
          padding: '32px 24px 20px',
          textAlign: 'center',
          position: 'relative',
        }}>
          <div style={{ position: 'absolute', top: 14, right: 16, opacity: 0.25, color: '#fff' }}>
            <Sparkles size={22} />
          </div>
          <div style={{
            width: 60,
            height: 60,
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px',
            border: '3px solid rgba(255,255,255,0.3)',
          }}>
            <Check size={28} color="#fff" strokeWidth={3} />
          </div>
          <h3 style={{ margin: '0 0 4px', fontFamily: T.display, fontStyle: 'italic', fontSize: 26, fontWeight: 500, color: '#fff' }}>
            Termin gebucht!
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.8)', fontFamily: T.body }}>
            Wir freuen uns auf Sie
          </p>
        </div>

        {/* Details */}
        <div style={{ padding: '20px 24px', display: 'grid', gap: 14 }}>
          {service && <DetailRow icon={<Sparkles size={16} />} label="Behandlung" value={service.name} />}
          {location && <DetailRow icon={<MapPin size={16} />} label="Standort" value={location.name} />}
          {date && (
            <DetailRow
              icon={<Calendar size={16} />}
              label="Datum"
              value={date.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            />
          )}
          {time && <DetailRow icon={<Clock size={16} />} label="Uhrzeit" value={`${time} Uhr`} />}
        </div>

        {/* Info + Buchungsnummer */}
        <div style={{ padding: '0 24px 8px' }}>
          <p style={{ margin: 0, fontSize: 12, color: T.muted, fontFamily: T.body, textAlign: 'center', lineHeight: 1.5 }}>
            {customerEmail
              ? `Bestätigung wird an ${customerEmail} gesendet.`
              : 'Eine Bestätigungs-E-Mail wird in Kürze gesendet.'}
          </p>
          {bookingId && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: T.muted, fontFamily: T.body, textAlign: 'center' }}>
              Buchungsnummer: <strong style={{ color: T.text }}>#{bookingId}</strong>
            </p>
          )}
        </div>

        {/* Buttons */}
        <div style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {onAddAnother && (
            <button
              onClick={onAddAnother}
              style={{
                width: '100%', padding: '13px 0',
                background: T.accent, border: 'none',
                borderRadius: T.r.md, fontFamily: T.body,
                fontSize: 15, fontWeight: 600, color: '#fff', cursor: 'pointer',
              }}
            >
              Weiteren Termin buchen
            </button>
          )}
          <a
            href="/portal"
            style={{
              display: 'block', width: '100%', padding: '11px 0', textAlign: 'center',
              background: 'transparent', border: `1px solid ${T.accent}`,
              borderRadius: T.r.md, fontFamily: T.body,
              fontSize: 14, fontWeight: 600, color: T.accent,
              textDecoration: 'none', boxSizing: 'border-box',
            }}
          >
            Mein Konto ansehen →
          </a>
          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '11px 0',
              background: 'transparent', border: `1px solid ${T.line}`,
              borderRadius: T.r.md, fontFamily: T.body,
              fontSize: 14, fontWeight: 500, color: T.muted, cursor: 'pointer',
            }}
          >
            {onAddAnother ? 'Fertig' : 'Schließen'}
          </button>
        </div>
      </div>
    </div>
  );
});

SuccessMessage.displayName = 'SuccessMessage';

export default SuccessMessage;
